import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";

class APIProductsFile extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  postImport = async (params: {
    text: string | null;
    file: File | null;
  }): Promise<any> => {
    let res = await this._fetch({
      url: `/products/import`,
      method: "POST",
      body: params,
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}

export default class APIProducts extends CompositionClient {
  file: APIProductsFile;

  constructor(client: AbstractClient) {
    super(client);
    this.file = new APIProductsFile(client);
  }

  getProducts = async (
    params: {
      page?: number;
      size?: number;
      load_all?: boolean;
      search?: string;
      include_children?: boolean;
      exclude_variants?: boolean;
      include_sleeping?: boolean;
      sleeping_only?: boolean;
      user_products_only?: boolean;
      sort_by?: string;
      sort_order?: string;
      modified_since?: string; // ISO timestamp for delta/incremental fetching
    } = {},
  ): Promise<types.PaginatedProductsResponse> => {
    // Default to user products only unless explicitly set to false
    const { user_products_only = true, ...otherParams } = params;
    const finalParams = {
      ...otherParams,
      user_products_only,
    };
    let res = await this._fetch({
      url: `/products/`,
      method: "GET",
      params: finalParams,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Alias for getProducts for consistency with other API clients
  get = async (
    params: {
      page?: number;
      size?: number;
      search?: string;
      include_children?: boolean;
      exclude_variants?: boolean;
      include_sleeping?: boolean;
      sleeping_only?: boolean;
      user_products_only?: boolean;
      sort_by?: string;
      sort_order?: string;
    } = {},
  ): Promise<types.PaginatedProductsResponse> => {
    return this.getProducts(params);
  };

  getProductById = async (productId: UUID): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putProductId = async (
    productId: UUID,
    { ...body }: types.ProductUpdateRequest,
  ): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteProductId = async (
    productId: UUID,
  ): Promise<{ status: string; message: string }> => {
    let res = await this._fetch({
      url: `/products/${productId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putProductIdSleep = async (
    productId: UUID,
  ): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/sleep`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putProductIdUnsleep = async (
    productId: UUID,
  ): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/unsleep`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postProducts = async ({
    ...body
  }: types.ProductCreateRequest): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getProductBySku = async (sku: string): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/sku/${sku}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getProductByCode = async (code: string): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/code/${code}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postProductIdUploadImages = async (
    productId: UUID,
    files: File[],
  ): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/upload-images`,
      method: "POST",
      body: { files },
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteProductIdImages = async (
    productId: UUID,
    imageUrls: string[],
  ): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/images`,
      method: "DELETE",
      body: imageUrls,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getProductStockSummary = async (
    productId: UUID,
  ): Promise<types.ProductStockSummary> => {
    let res = await this._fetch({
      url: `/products/${productId}/stock-summary`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getMedia = async (filename: string): Promise<Blob> => {
    let res = await this._fetch({
      url: `/products/media/${filename}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  // Batch fetch multiple media files at once
  getMediaBatch = async (
    filenames: string[],
  ): Promise<Record<string, Blob>> => {
    let res = await this._fetch({
      url: `/products/media/batch`,
      method: "POST",
      body: { filenames },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });

    if (res.headers.get("Content-Type") === "application/json") {
      const data = await res.json();
      // Convert base64 strings back to Blobs
      const result: Record<string, Blob> = {};
      for (const [filename, base64] of Object.entries(data)) {
        if (typeof base64 === "string") {
          // Decode base64 to binary
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          result[filename] = new Blob([bytes], { type: "image/jpeg" });
        }
      }
      return result;
    }
    throw new Error("Bad content type");
  };

  // User Products Methods
  getUserProducts = async (): Promise<types.ProductResponse[]> => {
    let res = await this._fetch({
      url: `/user-products`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  checkUserProduct = async (
    productId: UUID,
  ): Promise<{ is_user_product: boolean }> => {
    let res = await this._fetch({
      url: `/user-products/check/${productId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  addUserProduct = async (productId: UUID): Promise<{ message: string }> => {
    let res = await this._fetch({
      url: `/user-products/${productId}`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  removeUserProduct = async (productId: UUID): Promise<{ message: string }> => {
    let res = await this._fetch({
      url: `/user-products/${productId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  toggleUserProduct = async (productId: UUID): Promise<{ added: boolean }> => {
    let res = await this._fetch({
      url: `/user-products/${productId}/toggle`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  batchAddUserProducts = async (
    productIds: UUID[],
  ): Promise<{ added_count: number }> => {
    let res = await this._fetch({
      url: `/user-products/batch/add`,
      method: "PUT",
      body: productIds,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // =============================================================================
  // Product CRM Methods
  // =============================================================================

  getProductCRMStatistics = async (
    productId: UUID,
    params: types.ProductCRMFilters = {},
  ): Promise<types.ProductCRMStatisticsResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/crm/statistics`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getProductCRMPartners = async (
    productId: UUID,
    params: types.ProductPartnerFilters = {},
  ): Promise<types.PaginatedPartnerStatisticsResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/crm/partners`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getProductCRMRolling = async (
    productId: UUID,
    params: types.ProductRollingFilters,
  ): Promise<types.ProductRollingAggregatesResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/crm/rolling`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getProductCRMOrders = async (
    productId: UUID,
    params: types.ProductOrdersFilters = {},
  ): Promise<types.ProductOrdersResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/crm/orders`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getProductRelevance = async (
    days: number = 30,
  ): Promise<types.ProductRelevance[]> => {
    let res = await this._fetch({
      url: `/products/relevance`,
      method: "GET",
      params: { days },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
